import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import meetingAgendaService from '../services/meetingAgenda.service';
/** True while a preparation is still in flight. */
export const isAgendaJobRunning = (job) => job?.status === 'queued' || job?.status === 'processing';
/**
 * Pull the real server message out of whatever ApiCall handed back.
 *
 * ApiCall RETURNS the AxiosError on a non-2xx rather than throwing, so on a
 * failure `res.data` is undefined and the server's message is one level deeper
 * at `res.response.data.message`. Reading only the success shape is what
 * reduced every backend error — "No synced performance data for…", "You do not
 * have permission…", the worker's own failure reason — to a generic fallback.
 */
export function apiErrorMessage(res, fallback) {
    const msg = res?.response?.data?.message ?? // AxiosError returned by ApiCall
        res?.data?.message ?? // a 2xx body with success: false
        (typeof res?.message === 'string' ? res.message : undefined); // raw Error
    // Axios's own "Request failed with status code 422" says nothing useful.
    if (!msg || /^Request failed with status code/i.test(msg))
        return fallback;
    return msg;
}
const initialState = {
    agendas: [],
    total: 0,
    page: 1,
    limit: 20,
    loading: false,
    error: null,
    saving: false,
    preparing: null,
    deleting: false,
    currentAgenda: null,
    currentAgendaLoading: false,
    visibleClients: [],
    visibleClientsLoading: false,
    clientMeetings: [],
    clientMeetingsLoading: false,
    clientMeetingsError: null,
};
// ══════════════════════════════════════════════════════════════════════════
// THUNKS
// ══════════════════════════════════════════════════════════════════════════
export const fetchAgendas = createAsyncThunk('meetingAgenda/fetchAgendas', async (params = {}, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const response = await meetingAgendaService.listAgendas(params, token || '');
        if (response?.data?.success) {
            return response.data.data;
        }
        return rejectWithValue(apiErrorMessage(response, 'Failed to fetch meeting agendas'));
    }
    catch (err) {
        return rejectWithValue(apiErrorMessage(err, 'Failed to fetch meeting agendas'));
    }
});
export const createAgendaThunk = createAsyncThunk('meetingAgenda/createAgenda', async (payload, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const response = await meetingAgendaService.createAgenda(payload, token || '');
        const body = response?.data;
        if (!body?.success) {
            return rejectWithValue(apiErrorMessage(response, 'Failed to create meeting agenda'));
        }
        return { agenda: body.data, message: body.message };
    }
    catch (err) {
        return rejectWithValue(apiErrorMessage(err, 'Failed to create meeting agenda'));
    }
});
export const prepareAgendaThunk = createAsyncThunk('meetingAgenda/prepareAgenda', async (agendaId, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const response = await meetingAgendaService.prepareAgenda(agendaId, token || '');
        const body = response?.data;
        if (!body?.success) {
            return rejectWithValue(apiErrorMessage(response, 'Failed to prepare the agenda'));
        }
        return { agenda: body.data, message: body.message };
    }
    catch (err) {
        return rejectWithValue(apiErrorMessage(err, 'Failed to prepare the agenda'));
    }
});
/**
 * Polled by the list while a preparation runs. Deliberately not tracked in
 * `loading` — it fires every few seconds and must not flicker the table.
 */
export const fetchAgendaJobStatus = createAsyncThunk('meetingAgenda/fetchAgendaJobStatus', async (agendaId, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const response = await meetingAgendaService.getAgendaJobStatus(agendaId, token || '');
        const body = response?.data;
        if (!body?.success) {
            return rejectWithValue(apiErrorMessage(response, 'Failed to read preparation status'));
        }
        return body.data;
    }
    catch (err) {
        return rejectWithValue(apiErrorMessage(err, 'Failed to read preparation status'));
    }
});
export const updateAgendaDocumentThunk = createAsyncThunk('meetingAgenda/updateAgendaDocument', async ({ agendaId, documents }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const response = await meetingAgendaService.updateAgendaDocument(agendaId, documents, token || '');
        const body = response?.data;
        if (!body?.success) {
            return rejectWithValue(apiErrorMessage(response, 'Failed to save the document'));
        }
        return { agenda: body.data, message: body.message };
    }
    catch (err) {
        return rejectWithValue(apiErrorMessage(err, 'Failed to save the document'));
    }
});
export const fetchAgendaById = createAsyncThunk('meetingAgenda/fetchAgendaById', async (agendaId, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const response = await meetingAgendaService.getAgendaById(agendaId, token || '');
        const body = response?.data;
        if (!body?.success) {
            return rejectWithValue(apiErrorMessage(response, 'Failed to fetch meeting agenda'));
        }
        return body.data;
    }
    catch (err) {
        return rejectWithValue(apiErrorMessage(err, 'Failed to fetch meeting agenda'));
    }
});
export const deleteAgendaThunk = createAsyncThunk('meetingAgenda/deleteAgenda', async (agendaId, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const response = await meetingAgendaService.deleteAgenda(agendaId, token || '');
        const body = response?.data;
        if (!body?.success) {
            return rejectWithValue(apiErrorMessage(response, 'Failed to delete meeting agenda'));
        }
        return { agendaId, message: body.message };
    }
    catch (err) {
        return rejectWithValue(apiErrorMessage(err, 'Failed to delete meeting agenda'));
    }
});
export const fetchClientMeetings = createAsyncThunk('meetingAgenda/fetchClientMeetings', async ({ clientId, start, end }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const response = await meetingAgendaService.getClientMeetings(clientId, token || '', { start, end });
        const body = response?.data;
        if (!body?.success) {
            return rejectWithValue(apiErrorMessage(response, 'Failed to fetch meetings'));
        }
        return body.data || [];
    }
    catch (err) {
        return rejectWithValue(apiErrorMessage(err, 'Failed to fetch meetings'));
    }
});
export const fetchVisibleClients = createAsyncThunk('meetingAgenda/fetchVisibleClients', async (_, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token;
        const response = await meetingAgendaService.getVisibleClients(token || '');
        const body = response?.data;
        if (!body?.success) {
            return rejectWithValue(apiErrorMessage(response, 'Failed to fetch clients'));
        }
        const clients = body.data || [];
        return clients.filter((c) => c.visible !== false);
    }
    catch (err) {
        return rejectWithValue(apiErrorMessage(err, 'Failed to fetch clients'));
    }
});
// ══════════════════════════════════════════════════════════════════════════
// SLICE
// ══════════════════════════════════════════════════════════════════════════
const meetingAgendaSlice = createSlice({
    name: 'meetingAgenda',
    initialState,
    reducers: {
        clearCurrentAgenda(state) {
            state.currentAgenda = null;
        },
        clearClientMeetings(state) {
            state.clientMeetings = [];
            state.clientMeetingsError = null;
            state.clientMeetingsLoading = false;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchAgendas.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(fetchAgendas.fulfilled, (state, action) => {
            state.loading = false;
            state.agendas = action.payload.items || [];
            state.total = action.payload.total || 0;
            state.page = action.payload.page || 1;
            state.limit = action.payload.limit || 20;
        })
            .addCase(fetchAgendas.rejected, (state, action) => {
            state.loading = false;
            state.agendas = [];
            state.total = 0;
            state.error =
                action.payload ||
                    action.error.message ||
                    'Failed to fetch meeting agendas';
        });
        builder
            .addCase(createAgendaThunk.pending, (state) => {
            state.saving = true;
        })
            .addCase(createAgendaThunk.fulfilled, (state) => {
            state.saving = false;
        })
            .addCase(createAgendaThunk.rejected, (state) => {
            state.saving = false;
        });
        builder
            .addCase(prepareAgendaThunk.pending, (state, action) => {
            state.preparing = action.meta.arg;
        })
            // Prepare now only QUEUES the work — the documents don't exist
            // yet. Patch the row's job so it shows "Queued…" and the list
            // starts polling; has_report flips when the job reports done.
            .addCase(prepareAgendaThunk.fulfilled, (state, action) => {
            state.preparing = null;
            const idx = state.agendas.findIndex((a) => a._id === action.payload.agenda._id);
            if (idx !== -1) {
                state.agendas[idx].job = action.payload.agenda.job ?? {
                    id: null,
                    status: 'queued',
                    error: null,
                    queued_at: new Date().toISOString(),
                    started_at: null,
                    finished_at: null,
                };
                state.agendas[idx].updated_at = action.payload.agenda.updated_at;
            }
        })
            .addCase(prepareAgendaThunk.rejected, (state) => {
            state.preparing = null;
        });
        builder
            .addCase(fetchAgendaById.pending, (state) => {
            state.currentAgendaLoading = true;
        })
            .addCase(fetchAgendaById.fulfilled, (state, action) => {
            state.currentAgendaLoading = false;
            state.currentAgenda = action.payload;
        })
            // Without this the open drawer keeps the pre-edit report, so
            // clearing the local draft after a save snapped the text back to
            // the old version — the save had worked, the view just never
            // heard about it.
            .addCase(fetchAgendaJobStatus.fulfilled, (state, action) => {
            const { _id, job, status, has_report, updated_at } = action.payload;
            const idx = state.agendas.findIndex((a) => a._id === _id);
            if (idx === -1)
                return;
            state.agendas[idx].job = job;
            state.agendas[idx].status = status;
            state.agendas[idx].has_report = has_report;
            state.agendas[idx].updated_at = updated_at;
        })
            .addCase(updateAgendaDocumentThunk.fulfilled, (state, action) => {
            const saved = action.payload.agenda;
            state.currentAgenda = saved;
            const idx = state.agendas.findIndex((a) => a._id === saved._id);
            if (idx !== -1) {
                state.agendas[idx].edited_at = saved.edited_at;
                state.agendas[idx].edited_by_name = saved.edited_by_name;
                state.agendas[idx].updated_at = saved.updated_at;
            }
        })
            .addCase(fetchAgendaById.rejected, (state) => {
            state.currentAgendaLoading = false;
            state.currentAgenda = null;
        });
        builder
            .addCase(deleteAgendaThunk.pending, (state) => {
            state.deleting = true;
        })
            .addCase(deleteAgendaThunk.fulfilled, (state, action) => {
            state.deleting = false;
            state.agendas = state.agendas.filter((a) => a._id !== action.payload.agendaId);
            state.total = Math.max(0, state.total - 1);
        })
            .addCase(deleteAgendaThunk.rejected, (state) => {
            state.deleting = false;
        });
        builder
            .addCase(fetchClientMeetings.pending, (state) => {
            state.clientMeetingsLoading = true;
            state.clientMeetingsError = null;
            state.clientMeetings = [];
        })
            .addCase(fetchClientMeetings.fulfilled, (state, action) => {
            state.clientMeetingsLoading = false;
            state.clientMeetings = action.payload;
        })
            .addCase(fetchClientMeetings.rejected, (state, action) => {
            state.clientMeetingsLoading = false;
            state.clientMeetings = [];
            state.clientMeetingsError =
                action.payload ||
                    action.error.message ||
                    'Failed to fetch meetings';
        });
        builder
            .addCase(fetchVisibleClients.pending, (state) => {
            state.visibleClientsLoading = true;
        })
            .addCase(fetchVisibleClients.fulfilled, (state, action) => {
            state.visibleClientsLoading = false;
            state.visibleClients = action.payload;
        })
            .addCase(fetchVisibleClients.rejected, (state) => {
            state.visibleClientsLoading = false;
        });
    },
});
export const { clearCurrentAgenda, clearClientMeetings } = meetingAgendaSlice.actions;
// ══════════════════════════════════════════════════════════════════════════
// SELECTORS
// ══════════════════════════════════════════════════════════════════════════
export const selectAgendas = (s) => s.meetingAgenda?.agendas || [];
export const selectAgendasLoading = (s) => !!s.meetingAgenda?.loading;
export const selectAgendasTotal = (s) => s.meetingAgenda?.total || 0;
export const selectAgendaSaving = (s) => !!s.meetingAgenda?.saving;
export const selectAgendaPreparing = (s) => s.meetingAgenda?.preparing || null;
export const selectAgendaDeleting = (s) => !!s.meetingAgenda?.deleting;
export const selectCurrentAgenda = (s) => s.meetingAgenda?.currentAgenda || null;
export const selectCurrentAgendaLoading = (s) => !!s.meetingAgenda?.currentAgendaLoading;
export const selectAgendaVisibleClients = (s) => s.meetingAgenda?.visibleClients || [];
export const selectAgendaVisibleClientsLoading = (s) => !!s.meetingAgenda?.visibleClientsLoading;
export const selectClientMeetings = (s) => s.meetingAgenda?.clientMeetings || [];
export const selectClientMeetingsLoading = (s) => !!s.meetingAgenda?.clientMeetingsLoading;
export const selectClientMeetingsError = (s) => s.meetingAgenda?.clientMeetingsError || null;
export default meetingAgendaSlice.reducer;
